import React, { useEffect } from 'react'
import MenuLayout from './modules/layout/components/menu-layout'
import { Navigate, Outlet, Route, useLocation, useMatches, useNavigate, useOutletContext, useSearchParams } from 'react-router'
import ErrorBoundary from './ErrorBoundary'

import articleRoutes from '@/modules/article/article-routes'
import myAccountRoutes from '@/modules/my-account/my-account-routes'
import bankingRoutes from '@/modules/banking/banking-routes'
import settingsRoutes from '@/modules/settings/setting-routes'
import documentRoutes from '@/modules/document/document-routes'
import dashboardRoutes from '@/modules/dashboard/dashboard-routes'
import backupRoutes from '@/modules/backup/backup-routes'
import customerRoutes from '@/modules/customer/customer-routes'
import supplierRoutes from '@/modules/supplier/supplier-routes'
import bookingRoutes from '@/modules/booking/booking-routes'
import expenseRoutes from '@/modules/expense/expense-routes'
import revenueRoutes from '@/modules/revenue/revenue-routes'
import documentExportRoutes from '@/modules/document-export/document-export-routes'
import taxFormsRoutes from '@/modules/taxForms/taxForms-routes'
import reportingRoutes from './modules/reporting/reporting-routes'
import economicAssetRoutes from './modules/economicAsset/economicAsset-routes'
import shopRoutes from './modules/moduleSubscription/shop-routes'
import importRoutes from '@/modules/import/import-routes'
import todoRoutes from '@/modules/todo/todo-routes'
import transactionLetterRoutes from '@/modules/transactionLetter/transaction-letter-routes'
import accountRoutes from './modules/account/account-routes'
import PublicAccessLayout from './modules/layout/components/public-access-layout'
import invoiceRoutes from '@/modules/invoiceV2/invoice-routes'
import depositInvoiceRoutes from '@/modules/depositInvoiceV2/deposit-invoice-routes'
import recurringInvoiceRoutes from '@/modules/recurringInvoiceV2/recurring-invoice-routes'
import creditNoteRoutes from '@/modules/credit-note/credit-note-routes'
import cancellationRoutes from '@/modules/cancellation/cancellation-routes'
import manualCreditNoteRoutes from '@/modules/manualCreditNote/manual-credit-note-routes'
import dunningRoutes from '@/modules/dunning/dunning-routes'
import projectRoutes from '@/modules/projectV2/project-routes'
import closingInvoiceRoutes from '@/modules/closingInvoiceV2/closing-invoice-routes'
import appsRoutes from '@/modules/apps/apps-routes'
import clientRoutes from '@/modules/client/client-routes'
import timetrackingRoutes from '@/modules/timetracking/timetracking-routes'
import adminRoutes from '@/modules/admin/admin-routes'
import autoLoginRoutes from '@/modules/admin/auto-login-route'

import { initGlobalRouter } from '@/modules/layout/util/useInterceptedNavigate'
import ProtectedRoute from './protected-route'
import store, { useAppDispatch, useAppSelector } from './redux/store'
import { setContext } from './redux/ducks/moduleContext/current'
import { mapRouteToUserRight } from './modules'
import offerRoutes from '@/modules/offer/offer-routes'
import deliveryNoteRoutes from '@/modules/deliveryNote/delivery-note-routes'
import orderRoutes from '@/modules/orderV2/order-routes'
import purchaseRoutes from '@/modules/purchase/purchase-routes'
import partialInvoiceRoutes from '@/modules/partialInvoice/partial-invoice-routes'
import { user } from 'ampersand-app'
import Loader from './modules/base/components/loader/loader'
import subscriptionRoutes from './modules/subscription/subscription-routes'
import BlankLayout from './modules/layout/components/blank-layout'
import setDocumentTitle from './utils/setDocumentTitle'
import { CustomHandle, RouteOptions } from './types/react-router'
import staffRoutes from './modules/staff/staff-routes'
import outlookRedirectRoutes from './modules/redirect/outlook-redirect-routes'
import openAppAdviceModal, { closeAppAdviceModal } from '@/modules/dashboard/helpers/openAppAdviceModal'
import { isAndroid, isIos } from '@/modules/_helpers/detectMobile'
import { noMobileAdvice, ProductTypes, subscriptionTypes } from '@/modules/base/util/constants'
import webstorageService from '@/modules/_services/webstorage.service'
import webStorageKeyEnum from '@/enums/web-storage-key.enum'
import firstStepsRoutes from './modules/firstSteps/firstSteps-routes'
import { isMeinBuero } from './utils/build-env'
import { getPostLoginRedirectDecision, RedirectContext } from './utils/post-login-redirect'
import { captureBootstrapError } from './modules/account/model/login-bootstrap'
import restoreRoutes from './modules/restore-page/restore-routes'
import buhlKontoRoutes from '@/modules/buhl/buhl-konto/buhl-konto-routes'
import customerIssuesRoutes from '@/modules/customer-issues/customer-issues-routes'

// no redirect decision within this window means something upstream failed - go to the
// dashboard instead of leaving the user on a blank page with a loader
const INDEX_REDIRECT_TIMEOUT_MS = 15000

const IndexRedirect = () => {
  const navigate = useNavigate()
  const subscriptionDetails = useAppSelector(state => state.subscription.details)
  const user = useAppSelector(state => state.user.info)
  const isMB = isMeinBuero()
  const subscriptionLoading = useAppSelector(state => state.subscription.details.loading)
  const tenantRestore = useAppSelector(state => state.tenant.info.restore)

  useEffect(() => {
    if (tenantRestore && isMB) {
      navigate(`/restore/${tenantRestore.reqUid}`)
      return
    }

    const subscriptions = subscriptionDetails?.subscriptions || []
    const effectiveSubscriptions = isMB
      ? subscriptions.filter(sub => sub.type !== subscriptionTypes.FREE_2025)
      : subscriptions
    const { confirmation } = subscriptionDetails || {}
    const { onboardingFinished } = confirmation || {}

    const hasTrialSub = effectiveSubscriptions.some(s => s.isTrial === true)
    const hasPaidSub = effectiveSubscriptions.some(s => s.isTrial === false)
    const hasOnlyDocumentTrial = isMB
      && effectiveSubscriptions.length > 0
      && effectiveSubscriptions.every(s => s.isTrial === true && s.typeOfSubscription === ProductTypes.DOCUMENT)
    const isDemo = user?.isDemo || false

    const decision = getPostLoginRedirectDecision({
      context: RedirectContext.INDEX,
      isMeinBuero: isMB,
      subscriptionLoading,
      onboardingFinished,
      hasTrialSub,
      hasPaidSub,
      hasOnlyDocumentTrial,
      isDemo,
      defaultPath: '/dashboard',
    })

    if (decision.blockRedirect || !decision.target) {
      // self-heal: if the subscription fetch never settles we would spin here forever
      const timer = setTimeout(() => {
        captureBootstrapError('index-redirect-timeout', new Error(decision.reason))
        navigate('/dashboard', { replace: true })
      }, INDEX_REDIRECT_TIMEOUT_MS)

      return () => clearTimeout(timer)
    }

    navigate(decision.target, { replace: true })
    return undefined
  }, [subscriptionDetails, user, subscriptionLoading, navigate, tenantRestore])

  return <Loader />
}

export const allProtectedRoutes = (<>
  {firstStepsRoutes}
  {articleRoutes}
  {myAccountRoutes}
  {bankingRoutes}
  {settingsRoutes}
  {documentRoutes}
  {dashboardRoutes}
  {backupRoutes}
  {customerRoutes}
  {supplierRoutes}
  {bookingRoutes}
  {expenseRoutes}
  {revenueRoutes}
  {documentExportRoutes}
  {taxFormsRoutes}
  {reportingRoutes}
  {economicAssetRoutes}
  {shopRoutes}
  {todoRoutes}
  {transactionLetterRoutes}
  {timetrackingRoutes}
  {importRoutes}
  {invoiceRoutes}
  {depositInvoiceRoutes}
  {recurringInvoiceRoutes}
  {creditNoteRoutes}
  {cancellationRoutes}
  {manualCreditNoteRoutes}
  {dunningRoutes}
  {projectRoutes}
  {closingInvoiceRoutes}
  {appsRoutes}
  {clientRoutes}
  {offerRoutes}
  {deliveryNoteRoutes}
  {orderRoutes}
  {staffRoutes}
  {outlookRedirectRoutes}
  {purchaseRoutes}
  {partialInvoiceRoutes}
  {restoreRoutes}
  {customerIssuesRoutes}
</>)

const publicAccessRoutes = (<>
  {accountRoutes}
  {adminRoutes}
  {buhlKontoRoutes}
  {autoLoginRoutes}
</>)

const RootLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  initGlobalRouter(navigate)

  let routeOptions: RouteOptions = {}
  const matches = useMatches()

  if (matches) {
    const handle = matches?.at(-1)?.handle as CustomHandle
    routeOptions = handle?.options || {}

    if (handle?.title) {
      setDocumentTitle(handle?.title)
    }

    if (handle?.onNavigate) {
      handle?.onNavigate()
    }
  }

  useEffect(() => {
    dispatch(setContext({ module: mapRouteToUserRight(location.pathname) }))

    for (const route of noMobileAdvice) {
      if (location.pathname.startsWith(route)) {
        closeAppAdviceModal()
        return
      }
    }

    // Trigger mobile warning modal on route changes
    const subscriptions = store.getState().subscription.details.subscriptions
    const isTaxConsultant = subscriptions?.some(s => s.typeOfSubscription === ProductTypes.TAX_CONSULTANT)
    const overrideMobileWarning = webstorageService.getItem(webStorageKeyEnum.OVERRIDE_MOBILE_WARNING)
    const onboardingFinished = store.getState().user.info.finishedOnboarding
    if ((isAndroid() || isIos()) && onboardingFinished && !isTaxConsultant && !overrideMobileWarning) {
      openAppAdviceModal()
    }
  }, [location])

  return <Outlet context={{ routeOptions }} />
}

const Layout = () => {
  const { routeOptions } = useOutletContext<{ routeOptions: RouteOptions }>()
  const [searchParams] = useSearchParams()
  const printMode = searchParams.get('print')

  const layout = (printMode || user?.isDesktopTenant) ? <Outlet /> : <MenuLayout noBackgroundColor={routeOptions?.noBackgroundColor} />

  return (
    <>
      {layout}
    </>
  )
}

const AllRoutes = (
  <Route id='root' element={<RootLayout />}>
    <Route id='publicAccessLayout' element={<PublicAccessLayout />}>
      {publicAccessRoutes}
      <Route path="*" element={<Navigate replace to='/login' />} />
    </Route>
    <Route id='protectedRoute' element={<ProtectedRoute />}>
      <Route element={<Layout />}>
        <Route index Component={IndexRedirect} />
        <Route path='cashregister' Component={() => {
          const navigate = useNavigate()

          useEffect(() => {
            const cashRegister = ENV.product === 'meinbuero' ? '60bf3bc07f27994b862f31ac' : '60af93c5fc3fb11c222a6fa1'
            navigate(`/app/all/show/${cashRegister}`)
          }, [])

          return <Loader />
        }} />
        <Route element={<ErrorBoundary><Outlet /></ErrorBoundary>}>
          {allProtectedRoutes}
        </Route>
        <Route path="*" element={<Navigate replace to='/dashboard' />} />
      </Route>
      <Route id='blankLayout' element={<BlankLayout />}>
        {subscriptionRoutes}
      </Route>
    </Route>
  </Route>
)

export default AllRoutes
